/* eslint-disable */
import { useEffect, useRef } from 'react'
import { webSocketWorkerPriceEvent } from '../../../services'
import { routes } from '../../../utils/constants'
import axios from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import { setBloombergSnapshot } from '../../../redux/slices/Bloomberg/bloombergSlice'
import useAxios from '../../../hooks/useAxios'
import { createBloombergCurrency } from '../../../helper'
import MTMCalculator from '../MTMCalculator'
import FeeDollarNotional from '../FeeDollarNotional'

const useBloombergSetting = ({
  gridRef,
  priceRatesNormalize,
  setPriceRatesNormalize,
  tradeBlotterResponse,
  tradeBlotterDataAgGrid,
  priceRatesData,
  setPriceRatesData,
  setMenuPriceLoading,
  setBloombergConnection,
  bloombergPriceSnapshot,
  bloombergRefresh
}) => {
  const dispatch = useDispatch()
  const token = useSelector(state => state.auth.token)
  const { response } = useAxios({ endPoint: routes.currencyPair })
  const { data: currencyPairData } = response

  useEffect(() => {
    if (Array.isArray(currencyPairData) && currencyPairData.length > 0) {
      const brombergSymbol = currencyPairData.map(cp => createBloombergCurrency(cp))
      webSocketWorkerPriceEvent.sendEvent(brombergSymbol)
    }
  }, [currencyPairData])

  const setDataWithLogic = newData => {
    if (newData.type === undefined && newData.security !== 'connection_status') {
      setPriceRatesData(prevData => {
        const index = prevData?.findIndex(price => price.security === newData.security)
        if (index !== -1) {
          if (!isNaN(newData.LAST_PRICE) && newData.LAST_PRICE !== '') {
            if (newData.LAST_PRICE > prevData[index].LAST_PRICE) {
              newData.status = 'UP'
            } else if (newData.LAST_PRICE < prevData[index].LAST_PRICE) {
              newData.status = 'DOWN'
            } else {
              newData.status = prevData[index].status
            }
            const updatedData = [...prevData]
            updatedData[index] = newData
            const { base_currency, counter_currency } = newData
            const symbol = `${base_currency}${counter_currency}`
            setPriceRatesNormalize(prevNormalize => ({
              ...prevNormalize,
              [symbol]: newData
            }))
            return updatedData
          } else {
            return prevData
          }
        } else {
          if (!isNaN(newData.LAST_PRICE) && newData.LAST_PRICE !== '') {
            const { base_currency, counter_currency } = newData
            const symbol = `${base_currency}${counter_currency}`
            setPriceRatesNormalize(prevNormalize => ({
              ...prevNormalize,
              [symbol]: newData
            }))
            return [...prevData, newData]
          } else {
            return prevData
          }
        }
      })
    }
  }

  // Create the calculator instance outside useEffect so it persists
  const mtmCalculator = useRef(MTMCalculator)

  useEffect(() => {
    if (gridRef.current && gridRef.current.api && priceRatesNormalize) {
      let calculationsPerformed = 0
      let successfulCalculations = 0

      // Collect updated rows to send using AG Grid transactions
      const updatedTradesMap = {}
      const updatedNodesMap = {}

      tradeBlotterDataAgGrid.current.forEach(node => {
        if (
          node.children?.length > 0 &&
          node.staging_status === 'Live' &&
          (node.status === null || node.status === 'NEW')
        ) {
          if (['Spot', 'NDF', 'Forward', 'Swap'].includes(node.type)) {
            let dealTotalMTM = 0
            let validTradeCount = 0
            let tradeResult
            let dollarNotionalResult
            let totalDollarNotional = 0
            let dollarNotionalCount = 0
            let feeResult
            let totalFee = 0
            let feeCount = 0
            node.children.forEach(trade => {
              calculationsPerformed++
              if (node.type !== 'Swap') {
                tradeResult = mtmCalculator.current.calculateSpotNdfForwardTradeMTM(
                  trade,
                  priceRatesNormalize
                )
                dollarNotionalResult = FeeDollarNotional.calculateDollarNotional(
                  trade,
                  priceRatesNormalize
                )
              } else {
                //console.log('Calculating MTM for Swap trade:', trade)
                tradeResult = mtmCalculator.current.calculateSwapTradeMTM(
                  trade,
                  priceRatesNormalize
                )
                dollarNotionalResult = FeeDollarNotional.calculateDollarNotionalSwap(
                  trade,
                  priceRatesNormalize
                )
              }
              // Use the new method that calculates both client and market maker MTM

              if (tradeResult) {
                trade.pnl_usd_live = tradeResult.total_mtm
                trade.client_mtm = tradeResult.client_mtm
                trade.market_maker_mtm = tradeResult.market_maker_mtm
                trade.last_mtm_update = Date.now()

                // mark trade as updated
                if (trade.id !== undefined) updatedTradesMap[trade.id] = trade

                dealTotalMTM += tradeResult.total_mtm
                validTradeCount++
                successfulCalculations++
              }

              if (dollarNotionalResult) {
                trade.dollar_notional = dollarNotionalResult
                totalDollarNotional += Math.abs(dollarNotionalResult)
                dollarNotionalCount++

                // mark trade as updated
                if (trade.id !== undefined) updatedTradesMap[trade.id] = trade

                // Now we calculate the fees from the dollar notional
                feeResult = FeeDollarNotional.calculateFee(trade)
                if (feeResult) {
                  const tradeFee = Math.abs(feeResult)
                  trade.fee = parseFloat(tradeFee).toFixed(2) // Ensure fee is a string with two decimal places
                  totalFee += tradeFee
                  feeCount++
                }
              }
            })

            // Aggregate MTM at deal level
            if (validTradeCount > 0) {
              node.pnl_usd_live = dealTotalMTM
              node.last_mtm_update = Date.now()
              if (node.id !== undefined) updatedNodesMap[node.id] = node
            }
            if (dollarNotionalCount > 0) {
              node.dollar_notional = totalDollarNotional / dollarNotionalCount
              if (node.id !== undefined) updatedNodesMap[node.id] = node
            }
            if (feeCount > 0) {
              node.fee = totalFee.toFixed(2) // Ensure fee is a string with two decimal places
              if (node.id !== undefined) updatedNodesMap[node.id] = node
            }
          } else if (!['Spot', 'NDF', 'Forward', 'Swap'].includes(node.type)) {
            //node.pnl_usd_live = node.pnl
            let dealTotalPnL = 0
            let validTradeCount = 0
            let tradeResult
            let dollarNotionalResult
            let totalDollarNotional = 0
            let dollarNotionalCount = 0
            let feeResult
            let totalFee = 0
            let feeCount = 0
            node.children.forEach(trade => {
              calculationsPerformed++
              tradeResult = mtmCalculator.current.calculateVanillaExoticTradePnL(
                trade,
                priceRatesNormalize
              )
              dollarNotionalResult = FeeDollarNotional.calculateDollarNotional(
                trade,
                priceRatesNormalize
              )
              if (tradeResult) {
                trade.pnl_usd_live = tradeResult.premium
                trade.last_mtm_update = Date.now()

                // mark trade as updated
                if (trade.id !== undefined) updatedTradesMap[trade.id] = trade

                dealTotalPnL += tradeResult.premium
                validTradeCount++
                successfulCalculations++
              }
              if (dollarNotionalResult) {
                trade.dollar_notional = dollarNotionalResult
                totalDollarNotional += Math.abs(dollarNotionalResult)
                dollarNotionalCount++
                // mark trade as updated
                if (trade.id !== undefined) updatedTradesMap[trade.id] = trade

                // Now we calculate the fees from the dollar notional
                feeResult = FeeDollarNotional.calculateFee(trade)
                if (feeResult) {
                  const tradeFee = Math.abs(feeResult)
                  trade.fee = parseFloat(tradeFee).toFixed(2)
                  totalFee += tradeFee
                  feeCount++
                }
              }
            })
            if (validTradeCount > 0) {
              node.pnl_usd_live = dealTotalPnL
              node.last_mtm_update = Date.now()
              if (node.id !== undefined) updatedNodesMap[node.id] = node
            }
            if (dollarNotionalCount > 0) {
              node.dollar_notional = totalDollarNotional / dollarNotionalCount
              if (node.id !== undefined) updatedNodesMap[node.id] = node
            }
            if (feeCount > 0) {
              node.fee = totalFee.toFixed(2) // Ensure fee is a string with two decimal places
              if (node.id !== undefined) updatedNodesMap[node.id] = node
            }
          }
        }
      })

      // console.log(
      //   `MTM Calculations: ${successfulCalculations}/${calculationsPerformed} successful`
      // )

      // Build updates array from maps (preserve update order: trades first then parent nodes)
      const updates = []
      Object.keys(updatedTradesMap).forEach(k => updates.push(updatedTradesMap[k]))
      Object.keys(updatedNodesMap).forEach(k => updates.push(updatedNodesMap[k]))

      const api = gridRef.current.api
      if (updates.length > 0 && api) {
        // If server-side row model is used, try refreshing specific row nodes
        updates.forEach(row => {
          try {
            const rowNode = api.getRowNode && api.getRowNode(String(row.id))
            if (rowNode) api.refreshCells({ rowNodes: [rowNode], force: true })
          } catch (e) {
            console.error('Error refreshing row node:', e)
            // final fallback: full server-side refresh without purge
            if (api.refreshServerSide) api.refreshServerSide({ purge: false })
          }
        })
      }
    }
  }, [
    gridRef.current,
    tradeBlotterDataAgGrid.current,
    bloombergRefresh,
    priceRatesNormalize,
    tradeBlotterResponse
  ])

  useEffect(() => {
    const normalizeData = dataArray => {
      return dataArray.reduce((acc, item) => {
        const { base_currency, counter_currency } = item
        const symbol = `${base_currency}${counter_currency}`
        acc[symbol] = item
        return acc
      }, {})
    }

    // Handle connection stop to bloomberg
    const handleConnectionStatus = status => {
      setBloombergConnection(status)
      if (!status) {
        setPriceRatesData(prev =>
          prev.length > 0 ? prev.map(({ status, ...rest }) => rest) : prev
        )
      }
    }

    const handleSnapshotUpdate = async () => {
      const url = `${process.env.REACT_APP_SERVER_URL}${routes.currencyPrice}`
      const result = await axios.get(url, { headers: { Authorization: token } })
      setPriceRatesData(result.data)
      setPriceRatesNormalize(normalizeData(result.data))
      dispatch(setBloombergSnapshot(result.data))
      setMenuPriceLoading(false)
    }
    if (bloombergPriceSnapshot.length === 0) {
      handleSnapshotUpdate()
      return
    }
    const handelMessage = async ({ data, type }) => {
      if (!data || type === 'RPC') return
      const { security, status } = data
      if (security === 'connection_status') {
        handleConnectionStatus(status)
        if (status && bloombergPriceSnapshot.length > 0 && priceRatesData.length === 0) {
          setPriceRatesData(bloombergPriceSnapshot)
          setPriceRatesNormalize(normalizeData(bloombergPriceSnapshot))
          setMenuPriceLoading(false)
        }
        return
      }
      // Streaming price update
      setDataWithLogic(data)
      setMenuPriceLoading(false)

      // If snapshot was stored earlier, use it to normalize
      if (bloombergPriceSnapshot.length > 0 && status === false) {
        setPriceRatesData(bloombergPriceSnapshot)
        setPriceRatesNormalize(normalizeData(bloombergPriceSnapshot))
        setMenuPriceLoading(false)
      }
    }

    webSocketWorkerPriceEvent.addEventListener('message', handelMessage)

    return () => {
      webSocketWorkerPriceEvent.removeEventListener('message', handelMessage)
    }
  }, [webSocketWorkerPriceEvent, bloombergPriceSnapshot, currencyPairData])
}

export default useBloombergSetting
